import * as XLSX from 'xlsx';
import type {
  TransactionRegisterResponse,
  TransactionRegisterRow,
} from '../services/inventoryReportService';
import { fromMinorUnits } from './money';

// Money conversion happens ONLY here, at the export boundary: minor units →
// major decimal. Blank for null (no invented zeros). (REPORTING-CENTER.2.3)
const moneyMajor = (minor: number | null | undefined): number | '' =>
  minor == null ? '' : fromMinorUnits(minor);

/**
 * REPORTING-CENTER.2 — Inventory Transaction Register / Stock Card exports.
 *
 * Client-built XLSX (Register + Metadata sheets) and CSV. QUANTITY-ONLY — no
 * cost/value columns. Filters are echoed in header rows. Stock-card exports add
 * an Opening Balance line at the top, a Running Balance column, and a Closing
 * Balance line at the bottom.
 */

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};
const fmtDateOnly = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toISOString().slice(0, 10);
};
const numOrBlank = (n: number | null | undefined): number | '' => (n == null ? '' : n);
const sizeText = (r: TransactionRegisterRow): string =>
  r.sizeValue != null && r.sizeUnit ? `${r.sizeValue} ${r.sizeUnit}` : '';
const sanitize = (raw: string): string => raw.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Money columns (major units) sit after Running Balance. Recorded Unit Cost is
// an exports-only column (on screen it's the Unit Cost tooltip) — the movement's
// own at-posting cost, distinct from the current-basis Unit Cost.
const REGISTER_HEADERS = [
  'Posted At', 'Item', 'Short ID', 'Size', 'Type', 'Category', 'Subcategory', 'Location',
  'Movement Type', 'Qty In', 'Qty Out', 'Running Balance',
  'Unit Cost', 'Value', 'Recorded Unit Cost',
  'Cost Basis', // COSTING.1d — recorded/estimated basis per row
  'Source Type', 'Source Ref', 'Order Number', 'Posted By', 'Notes',
];

// COSTING.1d — friendly per-row cost-basis label for exports.
const COST_BASIS_LABEL: Record<string, string> = {
  ACTUAL: 'Recorded', GENESIS: 'Estimated (opening)', FALLBACK: 'Estimated (last known)', UNCOSTED: 'Uncosted',
};
const basisLabel = (b: string | null | undefined): string =>
  b ? (COST_BASIS_LABEL[b] ?? b) : 'Estimated (current)';

function rowToArray(r: TransactionRegisterRow, isStockCard: boolean): (string | number)[] {
  return [
    fmtDateTime(r.date),
    r.itemName ?? '',
    r.shortId ?? '',
    sizeText(r),
    r.itemType ?? '',
    r.category ?? '',
    r.subcategory ?? '',
    r.locationName ?? '',
    r.movementType,
    r.qtyIn || '',
    r.qtyOut || '',
    isStockCard ? numOrBlank(r.runningBalance) : '',
    moneyMajor(r.unitCost),
    moneyMajor(r.value),
    moneyMajor(r.recordedUnitCost),
    basisLabel(r.costBasis),
    r.sourceDocumentType ?? '',
    r.sourceDocumentId ?? '',
    r.sourceDocumentNumber ?? '',
    r.postedBy ?? '',
    r.notes ?? '',
  ];
}

// Stock-card Opening/Closing anchor row, positioned by header name so it stays
// correct as columns evolve: label in Item, balance in Running Balance, and the
// opening/closing value in the Value column.
function anchorRow(
  meta: TransactionRegisterResponse['meta'],
  label: string,
  balance: number,
  value: number | null,
): (string | number)[] {
  const arr: (string | number)[] = REGISTER_HEADERS.map(() => '');
  arr[REGISTER_HEADERS.indexOf('Item')] = label;
  arr[REGISTER_HEADERS.indexOf('Location')] = meta.locationName ?? '';
  arr[REGISTER_HEADERS.indexOf('Running Balance')] = balance;
  const v = moneyMajor(value);
  if (v !== '') arr[REGISTER_HEADERS.indexOf('Value')] = v;
  return arr;
}

function filterLines(meta: TransactionRegisterResponse['meta'], activatedAt?: string | null): string[][] {
  const f = meta.filters;
  return [
    ['Report', meta.reportName],
    ['Mode', meta.mode === 'STOCK_CARD' ? 'Stock Card' : 'Transaction Register'],
    ['Venue', meta.venueName ?? meta.venueId],
    ['From', fmtDateTime(meta.from)],
    ['To', fmtDateTime(meta.to)],
    ['Location', meta.locationName ?? (f.locationId ?? 'All locations')],
    ['Movement types', f.types && f.types.length ? f.types.join(', ') : 'All'],
    ['Item type', f.itemType ?? 'All'],
    ['Category filter', f.categoryId ?? ''],
    ['Subcategory filter', f.subcategoryId ?? ''],
    ['Search', f.q ?? ''],
    ['Total rows', String(meta.total)],
    ['Truncated', meta.truncated ? 'YES — export exceeds the row cap' : 'no'],
    ['Generated at', fmtDateTime(meta.generatedAt)],
    ['Cost basis', `${meta.costBasis ?? 'CURRENT_LAST_COST'} — Unit Cost / Value are the cost recorded when each movement posted, falling back to the item's current cost (labelled an estimate) where a movement carries none.`],
    // COSTING.1d — when cost tracking is active, state since when values are recorded.
    ...(activatedAt ? [['Cost tracking since', fmtDateOnly(activatedAt)]] : []),
    ['Revenue', 'Not reported here. A sale by the glass or by recipe posts one row per COMPONENT consumed, and the item the guest bought is not stock-bearing, so no row can carry the line\'s revenue without double-counting it across the others.'],
    ...(meta.stockCard ? [
      ['Stock card item', meta.stockCard.itemName ?? meta.stockCard.itemId],
      ['Opening balance', String(meta.stockCard.openingBalance)],
      ['Closing balance', String(meta.stockCard.closingBalance)],
      ['Opening value', meta.stockCard.openingValue == null ? '' : String(fromMinorUnits(meta.stockCard.openingValue))],
      ['Closing value', meta.stockCard.closingValue == null ? '' : String(fromMinorUnits(meta.stockCard.closingValue))],
    ] : []),
  ];
}

function buildFilename(meta: TransactionRegisterResponse['meta'], ext: string): string {
  const from = fmtDateOnly(meta.from);
  const to = fmtDateOnly(meta.to);
  if (meta.mode === 'STOCK_CARD' && meta.stockCard?.itemName) {
    return `stock-card-${sanitize(meta.stockCard.itemName)}-${from}-to-${to}.${ext}`;
  }
  const loc = meta.locationName ? `-${sanitize(meta.locationName)}` : '';
  return `transaction-register${loc}-${from}-to-${to}.${ext}`;
}

export function downloadTransactionRegisterXlsx(report: TransactionRegisterResponse, activatedAt?: string | null): void {
  const { meta, rows } = report;
  const isStockCard = meta.mode === 'STOCK_CARD';
  const wb = XLSX.utils.book_new();

  const aoa: (string | number)[][] = [REGISTER_HEADERS];
  if (isStockCard && meta.stockCard) {
    aoa.push(anchorRow(meta, 'OPENING BALANCE', meta.stockCard.openingBalance, meta.stockCard.openingValue));
  }
  for (const r of rows) aoa.push(rowToArray(r, isStockCard));
  if (isStockCard && meta.stockCard) {
    aoa.push(anchorRow(meta, 'CLOSING BALANCE', meta.stockCard.closingBalance, meta.stockCard.closingValue));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = REGISTER_HEADERS.map(h => ({ wch: Math.max(h.length + 2, 12) }));
  XLSX.utils.book_append_sheet(wb, ws, isStockCard ? 'Stock Card' : 'Register');

  const metaWs = XLSX.utils.aoa_to_sheet([['Field', 'Value'], ...filterLines(meta, activatedAt)]);
  metaWs['!cols'] = [{ wch: 22 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');

  XLSX.writeFile(wb, buildFilename(meta, 'xlsx'));
}

function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadTransactionRegisterCsv(report: TransactionRegisterResponse, activatedAt?: string | null): void {
  const { meta, rows } = report;
  const isStockCard = meta.mode === 'STOCK_CARD';
  const lines: string[] = [];

  // Echo filters as leading comment-style rows, then a blank line, then the table.
  for (const [k, v] of filterLines(meta, activatedAt)) lines.push(`${csvCell(k)},${csvCell(v)}`);
  lines.push('');
  lines.push(REGISTER_HEADERS.map(csvCell).join(','));
  if (isStockCard && meta.stockCard) {
    lines.push(anchorRow(meta, 'OPENING BALANCE', meta.stockCard.openingBalance, meta.stockCard.openingValue).map(csvCell).join(','));
  }
  for (const r of rows) lines.push(rowToArray(r, isStockCard).map(csvCell).join(','));
  if (isStockCard && meta.stockCard) {
    lines.push(anchorRow(meta, 'CLOSING BALANCE', meta.stockCard.closingBalance, meta.stockCard.closingValue).map(csvCell).join(','));
  }

  const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildFilename(meta, 'csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
